import React, { useEffect, useMemo, useState } from "react";
import api, { eur, fmtDate, STATUS_LABELS, STATUS_STYLES } from "../lib/api";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";
import { Input } from "./ui/input";
import { useBrand, brandSlug } from "../context/BrandContext";
import { Truck, Check, X, AlertCircle, FileDown, Clock, Ban, Trash2, FileText } from "lucide-react";
import { statusToStep } from "./OrderProgress";
import { NewOrderDialog } from "./NewOrderDialog";
import { toast } from "sonner";

const STEP_OPTIONS = [
  { value: "nouvelle", label: "Reçue" },
  { value: "en_preparation", label: "En préparation" },
  { value: "en_livraison", label: "En livraison" },
  { value: "livree", label: "Livrée" },
];

const stepValue = (status) => ["nouvelle", "en_preparation", "en_livraison", "livree"][statusToStep(status)];

const FILTERS = [
  { value: "tous", label: "Tous" },
  { value: "en_attente", label: "À valider" },
  ...STEP_OPTIONS,
  { value: "refusee", label: "Refusée" },
  { value: "annulee", label: "Annulée" },
  { value: "cancel", label: "Demandes d'annulation" },
];

function RefuseDialog({ order, onRefuse }) {
  const [open, setOpen] = useState(false);
  const [motif, setMotif] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await onRefuse(order, motif.trim());
      setOpen(false);
      setMotif("");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setMotif(""); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="text-destructive" data-testid={`refuse-order-${order.id}`}>
          <Ban className="mr-1 h-3.5 w-3.5" /> Refuser
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Refuser la commande {order.order_number}</DialogTitle>
          <DialogDescription>Indiquez le motif du refus. Il sera envoyé au distributeur.</DialogDescription>
        </DialogHeader>
        <Textarea
          data-testid="refuse-motif-input"
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
          placeholder="Ex : rupture de stock, minimum de commande non atteint…"
          rows={3}
        />
        <DialogFooter>
          <Button variant="destructive" onClick={submit} disabled={busy} data-testid="confirm-refuse-btn">
            {busy ? "..." : "Confirmer le refus"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BillingDialog({ order, onSaved }) {
  const [open, setOpen] = useState(false);
  const [lots, setLots] = useState([]);
  const [obs, setObs] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/orders/${order.id}/billing-info`, { lots, observations: obs });
      toast.success(`Informations de facture enregistrées — ${order.order_number}`);
      setOpen(false);
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) { setLots((order.items || []).map((i) => i.lot || "")); setObs(order.observations || ""); }
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" title="Informations de facture (lots, observations)" data-testid={`billing-info-${order.id}`}>
          <FileText className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Facture — commande {order.order_number}</DialogTitle>
          <DialogDescription>
            Ces informations apparaissent sur la facture : numéro de lot par ligne et observations (ex : « 3 PALETS — 2475 KG »).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {(order.items || []).map((it, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr] items-center gap-3">
              <span className="text-sm">{it.name} <span className="text-muted-foreground">× {it.quantity}</span></span>
              <Input
                data-testid={`lot-input-${order.id}-${i}`}
                value={lots[i] || ""}
                onChange={(e) => setLots((l) => l.map((v, j) => (j === i ? e.target.value : v)))}
                placeholder="N° de lot (ex : 20260914-OP7)"
                maxLength={60}
              />
            </div>
          ))}
          <Textarea
            data-testid={`observations-input-${order.id}`}
            value={obs}
            onChange={(e) => setObs(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder={"Observations\nEx : 3 PALETS\n2475 KG"}
          />
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy} data-testid={`save-billing-${order.id}`}>{busy ? "..." : "Enregistrer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuoteDialog({ order, onSaved }) {
  const [open, setOpen] = useState(false);
  const [prices, setPrices] = useState([]);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/orders/${order.id}/quote-prices`, {
        prices: order.items.map((it, i) => (it.price_on_request ? parseFloat(String(prices[i] ?? "").replace(",", ".")) : null)),
      });
      toast.success(`Devis chiffré — ${order.order_number}. Vous pouvez maintenant valider la commande.`);
      setOpen(false);
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setPrices(order.items.map(() => "")); }}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-amber-500 text-white hover:bg-amber-600" data-testid={`quote-order-${order.id}`}>Chiffrer</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Demande de devis — {order.order_number}</DialogTitle>
          <DialogDescription>
            Indiquez le prix unitaire (€) des articles « sur devis », selon les quantités demandées. Après enregistrement,
            convenez du prix avec le client, puis validez la commande : il recevra le montant final par email.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {order.items.map((it, i) => (
            <div key={i} className="grid grid-cols-[1fr_8rem] items-center gap-3">
              <span className="text-sm">{it.name} <span className="text-muted-foreground">× {it.quantity} {it.unit}</span></span>
              {it.price_on_request ? (
                <Input type="number" step="0.01" min="0" data-testid={`quote-price-${order.id}-${i}`} value={prices[i] ?? ""}
                  onChange={(e) => setPrices((p) => p.map((v, j) => (j === i ? e.target.value : v)))} placeholder="Prix unitaire €" />
              ) : (
                <span className="text-right text-sm tabular">{eur(it.unit_price)}</span>
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy} data-testid={`save-quote-${order.id}`}>{busy ? "..." : "Enregistrer les prix"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const OrderManager = ({ onChange }) => {
  const brand = useBrand();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("tous");

  const load = () => api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const refresh = async () => { await load(); onChange && onChange(); };

  const setStatus = async (order, value) => {
    if (value === stepValue(order.status)) return;
    await api.patch(`/orders/${order.id}/status`, { status: value });
    toast.success(`${order.order_number} → ${STATUS_LABELS[value]}`);
    await refresh();
  };
  const validateOrder = async (o) => {
    await api.post(`/orders/${o.id}/validate`);
    toast.success(`${o.order_number} validée`);
    await refresh();
  };
  const refuseOrder = async (o, motif) => {
    await api.post(`/orders/${o.id}/refuse`, { motif });
    toast.success(`${o.order_number} refusée`);
    await refresh();
  };
  const approveCancel = async (o) => {
    await api.patch(`/orders/${o.id}/cancel-approve`);
    toast.success(`${o.order_number} annulée`);
    await refresh();
  };
  const deleteOrder = async (o) => {
    try {
      await api.delete(`/orders/${o.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
      return;
    }
    toast.success(`Commande ${o.order_number} supprimée`);
    await refresh();
  };
  const rejectCancel = async (o) => {
    await api.patch(`/orders/${o.id}/cancel-reject`);
    toast.success(`Demande d'annulation refusée — ${o.order_number}`);
    await refresh();
  };

  const todayKey = new Date().toISOString().slice(0, 10);
  const todayOrders = orders.filter((o) => (o.created_at || "").slice(0, 10) === todayKey && !["annulee", "refusee"].includes(o.status));
  const todayRevenue = todayOrders.reduce((s, o) => s + o.total, 0);
  const pendingCancel = orders.filter((o) => o.cancel_requested).length;
  const pendingValidation = orders.filter((o) => o.status === "en_attente").length;

  const exportPdf = async () => {
    try {
      const res = await api.get("/export/orders-today.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `commandes-${brandSlug(brand.name)}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF généré");
    } catch {
      toast.error("Impossible de générer le PDF");
    }
  };

  const shown = useMemo(() => {
    if (filter === "tous") return orders;
    if (filter === "annulee") return orders.filter((o) => o.status === "annulee");
    if (filter === "refusee") return orders.filter((o) => o.status === "refusee");
    if (filter === "en_attente") return orders.filter((o) => o.status === "en_attente");
    if (filter === "cancel") return orders.filter((o) => o.cancel_requested);
    return orders.filter((o) => !["en_attente", "refusee", "annulee"].includes(o.status) && stepValue(o.status) === filter);
  }, [orders, filter]);

  return (
    <Card className="overflow-hidden" data-testid="order-manager">
      {/* Daily revenue recap */}
      <div className="flex flex-wrap items-center gap-8 border-b border-border bg-primary/5 px-5 py-4" data-testid="today-recap">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Chiffre d'affaires du jour</p>
          <p className="tabular font-display text-3xl font-extrabold text-primary" data-testid="today-revenue">{eur(todayRevenue)}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Commandes du jour</p>
          <p className="tabular font-display text-3xl font-extrabold">{todayOrders.length}</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {pendingValidation > 0 && (
            <button
              onClick={() => setFilter("en_attente")}
              className="flex items-center gap-2 rounded-md bg-amber-500/10 px-3 py-2 text-amber-700 transition-colors hover:bg-amber-500/15"
              data-testid="pending-validation-alert"
            >
              <Clock className="h-4 w-4" />
              <span className="text-sm font-semibold">{pendingValidation} commande(s) à valider</span>
            </button>
          )}
          {pendingCancel > 0 && (
            <button
              onClick={() => setFilter("cancel")}
              className="flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-destructive transition-colors hover:bg-destructive/15"
              data-testid="pending-cancel-alert"
            >
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm font-semibold">{pendingCancel} demande(s) d'annulation</span>
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
        <div>
          <h3 className="font-display font-bold">Gestion des commandes — tous les distributeurs</h3>
          <p className="text-xs text-muted-foreground">Validez les nouvelles commandes, changez le statut et gérez les annulations.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <NewOrderDialog onCreated={refresh} />
          <Button variant="outline" onClick={exportPdf} data-testid="export-pdf-btn">
            <FileDown className="mr-1.5 h-4 w-4" /> Exporter en PDF
          </Button>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-52" data-testid="order-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {FILTERS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>N°</TableHead>
              <TableHead>Dépôt</TableHead>
              <TableHead>Enseigne</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Livraison</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Changer le statut</TableHead>
              <TableHead>Annulation</TableHead>
              <TableHead className="text-right">Action rapide</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((o) => {
              const cancelled = o.status === "annulee";
              const refused = o.status === "refusee";
              const pending = o.status === "en_attente";
              const isDelivering = o.status === "en_livraison";
              const isDelivered = o.status === "livree";
              return (
                <TableRow key={o.id} data-testid={`manage-order-${o.id}`} className={o.cancel_requested ? "bg-destructive/5" : pending ? "bg-amber-500/5" : ""}>
                  <TableCell className="tabular font-medium">{o.order_number}</TableCell>
                  <TableCell>{o.client_name}</TableCell>
                  <TableCell className="text-sm font-medium text-foreground">{o.enseigne || "—"}</TableCell>
                  <TableCell className="tabular font-semibold">
                    {o.quote ? <Badge variant="outline" className="border-amber-500/50 bg-amber-500/10 text-amber-700">Devis à chiffrer</Badge> : eur(o.total)}
                  </TableCell>
                  <TableCell className="text-sm">{fmtDate(o.delivery_date)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_STYLES[o.status]} title={refused ? o.refus_motif : undefined}>
                      {STATUS_LABELS[o.status]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {pending ? (
                      <div className="flex items-center gap-1.5">
                        {o.quote ? (
                          <QuoteDialog order={o} onSaved={refresh} />
                        ) : (
                          <Button size="sm" onClick={() => validateOrder(o)} data-testid={`validate-order-${o.id}`}>
                            <Check className="mr-1 h-3.5 w-3.5" /> Valider
                          </Button>
                        )}
                        <RefuseDialog order={o} onRefuse={refuseOrder} />
                      </div>
                    ) : cancelled || refused ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : (
                      <Select value={stepValue(o.status)} onValueChange={(v) => setStatus(o, v)}>
                        <SelectTrigger className="w-40" data-testid={`status-select-${o.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STEP_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell>
                    {o.cancel_requested ? (
                      <div className="flex items-center gap-1.5">
                        <Button size="sm" variant="destructive" onClick={() => approveCancel(o)} data-testid={`approve-cancel-${o.id}`}>
                          <Check className="mr-1 h-3.5 w-3.5" /> Valider
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => rejectCancel(o)} data-testid={`reject-cancel-${o.id}`}>
                          <X className="mr-1 h-3.5 w-3.5" /> Refuser
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
                        disabled={cancelled || refused || pending || isDelivering || isDelivered}
                        onClick={() => setStatus(o, "en_livraison")}
                        data-testid={`quick-livraison-${o.id}`}
                      >
                        <Truck className="mr-1 h-4 w-4" /> En livraison
                      </Button>
                      <BillingDialog order={o} onSaved={refresh} />
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" className="text-destructive" title="Supprimer la commande" data-testid={`delete-order-${o.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Supprimer la commande {o.order_number} ?</AlertDialogTitle>
                            <AlertDialogDescription>
                              À utiliser pour une commande faite par erreur ({o.client_name}, {eur(o.total)}). L'action est définitive
                              et le distributeur n'est pas prévenu. Impossible si une facture a déjà été émise.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid="cancel-delete-order">Annuler</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteOrder(o)} data-testid={`confirm-delete-order-${o.id}`}>Supprimer</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {shown.length === 0 && (
              <TableRow><TableCell colSpan={9} className="py-8 text-center text-muted-foreground">Aucune commande.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};
